import * as React from 'react';
import * as _ from 'lodash';

import { css, OpenCSSProperties, StyleSheet } from 'aphrodite';
import { mixins, mobileUtils } from 'consumer-components';

import { ProvideTimers, TimerProps } from 'shared-react';
import { diagnostics } from 'ui-common';

import * as globalWrappers from 'login-utils/globalWrappers';

/**
 * This component renders its children, but when they're not completely on the screen,
 * it fixes them at the bottom of the screen to ensure they're exposed correctly to the user.
 */

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.padding(0),
    width: '100%',
  },
  visibleButton: {
    position: 'relative',
  },
  stickyButton: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
  },
});

export interface StickyButtonOwnProps {
  customBottomPadding?: string;
  customStickyButtonStyle?: OpenCSSProperties;
}

export interface StickyButtonState {
  buttonIsFullyVisible: boolean;
  referenceWidth: number;
  referenceHeight: number;
}

export class StickyButtonBase extends React.Component<TimerProps & StickyButtonOwnProps, StickyButtonState> {
  buttonRef: any;
  referenceElementRef: any;
  updateEvents = ['resize', 'touchmove', 'scroll', 'gesturechange'];

  constructor(props: any) {
    super(props);

    this.state = {
      buttonIsFullyVisible: true,
      referenceHeight: 0,
      referenceWidth: 0,
    };

    this.buttonRef = React.createRef();
    this.referenceElementRef = React.createRef();
  }

  componentDidMount() {
    const { children, reactSafeSetTimeout } = this.props;
    if (!children) throw new Error('StickyButton must have children.');

    reactSafeSetTimeout(() => {
      this.setState({
        referenceHeight: this.buttonRef.current.clientHeight,
        referenceWidth: this.buttonRef.current.clientWidth,
      });

      this.updateEvents.map(eventName =>
        globalWrappers.getWindow().addEventListener(eventName, this.updateVisibilityDebounced, true),
      );
      this.updateVisibility();
    });
  }

  componentWillUnmount() {
    this.updateEvents.map(eventName =>
      globalWrappers.getWindow().removeEventListener(eventName, this.updateVisibilityDebounced),
    );
  }

  updateVisibility = () => {
    const { customBottomPadding } = this.props;
    /* istanbul ignore if */
    if (!this.referenceElementRef.current) return;

    const windowHeight = globalWrappers.getWindow().innerHeight;
    // button bottom is at the children component's bottom position plus it's padding on the bottom if applicable
    const bottomPadding = globalWrappers.getWindow().innerHeight * parseFloat(customBottomPadding || '0%') / 100;
    const buttonBottom = this.referenceElementRef.current.getBoundingClientRect().bottom + bottomPadding;
    this.setState({
      buttonIsFullyVisible: windowHeight >= buttonBottom,
    });
  };

  /* istanbul ignore next */
  updateVisibilityDebounced = _.debounce(() => this.updateVisibility(), 15);

  render() {
    if (!mobileUtils.isMobile()) {
      diagnostics.warn('The StickyButton component is only intended for use on mobile.');
    }
    const { buttonIsFullyVisible } = this.state;
    const { children, customStickyButtonStyle } = this.props;

    return (
      <div>
        <div className={css(styles.wrapper)}>
          <div
            className={css(
              buttonIsFullyVisible ? styles.visibleButton : _.merge(styles.stickyButton, customStickyButtonStyle),
            )}
          >
            {React.Children.map(children, (element: any) => {
              return React.cloneElement(element, { ref: this.buttonRef });
            })}
          </div>
          <div
            ref={this.referenceElementRef}
            // extend children's height when button is not fully visible to avoid children height bounces when
            // scrolling down but not yet reach the fully visible check point
            style={
              !buttonIsFullyVisible
                ? {
                    height: this.state.referenceHeight,
                    width: this.state.referenceWidth,
                  }
                : {}
            }
          />
        </div>
      </div>
    );
  }
}

export const StickyButton = (props: StickyButtonOwnProps) => (
  <ProvideTimers render={timerProps => <StickyButtonBase {...timerProps} {...props} />} />
);
